import { useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TeamRole } from '@crossmatch/shared';
import { chatApi } from '../api/chat.js';
import { teamsApi } from '../api/teams.js';
import { usersApi } from '../api/users.js';
import type { TeamDto, UserLookupDto } from '../api/types.js';
import { toUserMessage } from '../api/errors.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { Collapsible } from '../components/Collapsible.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { EmptyState } from '../components/EmptyState.js';
import { ErrorState } from '../components/ErrorState.js';
import { GapPanel } from '../components/GapPanel.js';
import { Kanban, PhaseBar } from '../components/Kanban.js';
import { Loading } from '../components/Loading.js';
import { SkillPicker } from '../components/SkillPicker.js';
import { Tag } from '../components/Tag.js';
import { TeamChatPanel } from '../components/TeamChat.js';
import { WorkbenchPanel } from '../components/WorkbenchPanel.js';
import { formatDateTime, skillLabel } from '../lib/format.js';
import {
  MEMBER_STATUS_LABELS,
  ROLE_LABELS,
  ROLE_ORDER,
  teamStatusLabel,
  roleLabel,
} from '../lib/labels.js';
import { qk } from '../lib/queryKeys.js';
import { TeamHallPanel } from './TeamHall.js';
import { useMyTeamsQuery, useGapQuery, useProjectTasksQuery } from '../hooks/queries.js';
import { useSession } from '../store/session.js';
import { toast } from '../store/toast.js';

function memberNameOf(team: TeamDto, userId: string): string {
  const names = team.memberNames;
  if (names && typeof names[userId] === 'string' && names[userId].length > 0) return names[userId];
  return userId;
}

/**
 * 招募需求编辑（v0.2 T72）：仅队长且 forming 阶段可编辑。
 * skills 为空并保存 = 关闭招募（后端落 NULL）；成队后整块回退为只读展示。
 */
function RecruitEditor({ team, canEdit }: { team: TeamDto; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const current = team.recruit ?? null;
  const [skills, setSkills] = useState<string[]>(current?.skills ?? []);
  const [note, setNote] = useState(current?.note ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (input: { skills: string[]; note?: string }) => teamsApi.setRecruit(team.id, input),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: qk.myTeams() });
      void queryClient.invalidateQueries({ queryKey: qk.recruiting() });
      const recruit = data?.team?.recruit ?? null;
      setSkills(recruit?.skills ?? []);
      setNote(recruit?.note ?? '');
      setError(null);
      toast.success(recruit === null ? '招募已关闭' : '招募需求已保存', '已同步到「组队大厅」，别的同学可以按缺口申请加入');
    },
    onError: (err) => {
      setError(`保存招募需求失败：${toUserMessage(err)}`);
    },
  });

  const handleSave = (): void => {
    setError(null);
    save.mutate({ skills, note: note.trim() });
  };

  const handleClear = (): void => {
    setError(null);
    setSkills([]);
    setNote('');
    save.mutate({ skills: [] });
  };

  return (
    <div className="stack-sm">
      <span className="card-sub">招募需求（挂在「组队大厅」展示缺口，纯展示不进打分）</span>
      {current !== null ? (
        <div className="row-tight">
          <Tag tone="ok">招募中</Tag>
          {current.skills.map((skillId) => (
            <Tag key={skillId} tone="primary">
              {skillLabel(skillId)}
            </Tag>
          ))}
          {current.note.length > 0 ? <span className="muted small">{current.note}</span> : null}
        </div>
      ) : (
        <p className="muted small">未开放招募：填好需要的技能并保存后，别的同学会在「组队大厅」看到你们的招募卡。</p>
      )}
      {canEdit ? (
        <>
          {error !== null ? (
            <div className="banner banner-danger" role="alert">
              <span className="banner-ico" aria-hidden="true">
                !
              </span>
              <span className="wrap-anywhere">{error}</span>
            </div>
          ) : null}
          <SkillPicker
            selected={skills}
            onChange={setSkills}
            label="招募技能（最多 10 个；清空后保存 = 关闭招募）"
            hint="只接受标准技能词表；这些技能会作为招募卡标签展示，不影响匹配分数。"
            max={10}
          />
          <div className="field">
            <label className="field-label" htmlFor="recruit-note">
              招募说明（选填，200 字以内）
            </label>
            <input
              id="recruit-note"
              className="input"
              value={note}
              maxLength={200}
              placeholder="如：缺一位能独立做前端的同学，周末能线下碰头优先"
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
          <div className="row">
            <Button variant="secondary" size="sm" loading={save.isPending} onClick={handleSave}>
              {save.isPending ? '正在保存…' : current === null ? '开放招募' : '保存招募需求'}
            </Button>
            {current !== null ? (
              <Button variant="ghost" size="sm" disabled={save.isPending} onClick={handleClear}>
                关闭招募
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        <p className="field-hint">
          {team.status !== 'forming'
            ? '队伍已确认成队，招募需求随名单一起冻结（只读）。'
            : '只有队长可以设置招募需求。'}
        </p>
      )}
    </div>
  );
}

/** 队伍页：创建 / 邀请（userId）/ 接受 / 确认成队 → 展示后端生成的 4 条阶段待办。 */
export function TeamPage() {
  const me = useSession((state) => state.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const nameRef = useRef<HTMLInputElement>(null);

  const teamsQuery = useMyTeamsQuery();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [teamName, setTeamName] = useState('');
  const [teamEventCode, setTeamEventCode] = useState('');
  const [inviteUserId, setInviteUserId] = useState('');
  const [inviteRole, setInviteRole] = useState<TeamRole>('member');
  const [lookupResult, setLookupResult] = useState<UserLookupDto | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tab, setTab] = useState<'info' | 'chat' | 'gap' | 'hall' | 'board' | 'workbench'>('info');

  const teams = teamsQuery.data?.teams ?? [];
  const activeTeam = teams.find((team) => team.id === selectedId) ?? teams[0] ?? null;
  const projectId = activeTeam?.projectId ?? null;
  const projectTasksQuery = useProjectTasksQuery(projectId);
  const gapQuery = useGapQuery(activeTeam?.id ?? null);

  const createTeam = useMutation({
    mutationFn: (input: { name: string; eventCode?: string }) => teamsApi.create(input),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: qk.myTeams() });
      const created = data?.team;
      if (!created || typeof created.id !== 'string' || created.id.length === 0) {
        // 可见降级：不假装成功，明确告诉用户去哪一步继续
        toast.error('队伍已创建，但后端没有返回队伍 id', '已刷新队伍列表：请在下面手动选择刚创建的队伍');
        return;
      }
      setSelectedId(created.id);
      setTeamName('');
      setTeamEventCode('');
      setCreateError(null);
      toast.success(`队伍「${created.name}」已创建`, '下一步：邀请成员（按 userId），或直接确认成队');
    },
    onError: (error) => {
      setCreateError(`创建队伍失败：${toUserMessage(error)}`);
    },
  });

  const invite = useMutation({
    mutationFn: (input: { teamId: string; userId?: string; code?: string; role: TeamRole }) =>
      teamsApi.invite(input.teamId, { userId: input.userId, code: input.code, role: input.role }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.myTeams() });
      setInviteUserId('');
      setLookupResult(null);
      setInviteError(null);
      toast.success('邀请已发出', '对方登录后会在「我的队伍」里看到「接受邀请」按钮');
    },
    onError: (error) => {
      setInviteError(`邀请成员失败：${toUserMessage(error)}`);
    },
  });

  /** 按邀请 ID 查人（先确认是不是本人，再发邀请）。 */
  const lookup = useMutation({
    mutationFn: (code: string) => usersApi.lookup(code),
    onSuccess: (data) => {
      setLookupResult(data);
      setLookupError(null);
      setInviteError(null);
    },
    onError: (error) => {
      setLookupResult(null);
      setLookupError(`查找失败：${toUserMessage(error)}`);
    },
  });

  const accept = useMutation({
    mutationFn: (input: { teamId: string; userId: string }) => teamsApi.accept(input.teamId, input.userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.myTeams() });
      toast.success('已接受邀请', '现在你正式是这支队伍的成员了');
    },
  });

  const setRole = useMutation({
    mutationFn: (input: { teamId: string; userId: string; role: TeamRole }) =>
      teamsApi.setRole(input.teamId, input.userId, input.role),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: qk.myTeams() });
      toast.success('角色已更新', `新角色：${ROLE_LABELS[input.role]}`);
    },
  });

  const confirmTeam = useMutation({
    mutationFn: (teamId: string) => teamsApi.confirm(teamId),
    onSuccess: (data) => {
      setConfirmOpen(false);
      const nextProjectId = data?.project?.id;
      if (typeof nextProjectId === 'string' && nextProjectId.length > 0) {
        // 后端在成队时已生成待办，直接用返回值填充缓存，避免多余的一次请求
        queryClient.setQueryData(qk.projectTasks(nextProjectId), { tasks: data.tasks ?? [] });
      } else {
        toast.error('已成队，但后端没有返回 project.id', '无法自动加载阶段待办，请到「阶段待办」页查看');
      }
      void queryClient.invalidateQueries({ queryKey: qk.myTeams() });
      void queryClient.invalidateQueries({ queryKey: qk.myTasks() });
      toast.success('已成队', `后端已生成 ${data?.tasks?.length ?? 0} 条阶段待办`);
    },
  });

  /** 审批加入申请（扩展②） */
  const approveReq = useMutation({
    mutationFn: (input: { teamId: string; userId: string }) => teamsApi.approve(input.teamId, input.userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.myTeams() });
      toast.success('已通过申请', '对方现在是队伍成员了');
    },
  });

  /** 拒绝加入申请（扩展②） */
  const declineReq = useMutation({
    mutationFn: (input: { teamId: string; userId: string }) => teamsApi.decline(input.teamId, input.userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.myTeams() });
      toast.success('已拒绝申请', '对方可以在组队大厅找其他队伍');
    },
  });

  /** 修改准入档（扩展②） */
  const setPolicy = useMutation({
    mutationFn: (input: { teamId: string; policy: 'open' | 'approval' | 'invite_only' }) =>
      teamsApi.setJoinPolicy(input.teamId, input.policy),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: qk.myTeams() });
      void queryClient.invalidateQueries({ queryKey: ['hall'] });
      toast.success('准入档已更新', input.policy === 'invite_only' ? '队伍已从组队大厅下架' : '队伍对大厅可见');
    },
  });

  const handleCreate = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const name = teamName.trim();
    if (name.length === 0) {
      setCreateError('创建队伍失败：请填写队伍名称（例如「立信×东华 三人组」）');
      nameRef.current?.focus();
      return;
    }
    const input: { name: string; eventCode?: string } = { name };
    if (teamEventCode.trim().length > 0) input.eventCode = teamEventCode.trim().toUpperCase();
    setCreateError(null);
    createTeam.mutate(input);
  };

  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const handleLookup = (): void => {
    const raw = inviteUserId.trim();
    if (raw.length === 0) {
      setLookupError('请先填写对方的邀请 ID（形如 CM-XXXXXX）');
      setLookupResult(null);
      return;
    }
    if (UUID_RE.test(raw)) {
      setLookupError('这是 userId（UUID），可直接点「发送邀请」，无需查找');
      setLookupResult(null);
      return;
    }
    setLookupError(null);
    lookup.mutate(raw);
  };

  const handleInvite = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (activeTeam === null) return;
    const raw = inviteUserId.trim();
    if (raw.length === 0) {
      setInviteError('邀请成员失败：请填写对方的邀请 ID（形如 CM-XXXXXX），或对方的 userId');
      return;
    }
    // 已按 ID 查到本人 → 用查到的 userId 邀请（最精确）；
    // 否则按输入形状决定：UUID 走 userId，短 ID 走 code（后端解析）。
    const targetId = lookupResult?.userId ?? raw;
    if (me !== null && targetId === me.id) {
      setInviteError('邀请成员失败：不能邀请自己（你在队伍里已经是成员）');
      return;
    }
    setInviteError(null);
    if (lookupResult !== null) {
      invite.mutate({ teamId: activeTeam.id, userId: lookupResult.userId, role: inviteRole });
    } else if (UUID_RE.test(raw)) {
      invite.mutate({ teamId: activeTeam.id, userId: raw, role: inviteRole });
    } else {
      invite.mutate({ teamId: activeTeam.id, code: raw, role: inviteRole });
    }
  };

  if (teamsQuery.isPending) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">我的队伍</h1>
        </div>
        <Loading label="正在读取我的队伍…" rows={3} />
      </div>
    );
  }

  if (teamsQuery.isError) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">我的队伍</h1>
        </div>
        <ErrorState error={teamsQuery.error} title="读取队伍失败" onRetry={() => void teamsQuery.refetch()} />
      </div>
    );
  }

  const acceptedCount =
    activeTeam === null ? 0 : activeTeam.members.filter((member) => member.status === 'accepted').length;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-title">我的队伍</h1>
          <p className="page-sub">我拥有或已加入的队伍；确认成队后自动生成 4 条阶段待办。</p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => void teamsQuery.refetch()}>
          刷新队伍
        </Button>
      </div>

      {/* 待处理邀请横幅：被邀请人一进页面就看到，不用自己找 */}
      {(() => {
        const pendingInvites = teams.filter((team) =>
          team.members.some((m) => me !== null && m.userId === me.id && m.status === 'invited'),
        );
        if (pendingInvites.length === 0) return null;
        return (
          <div className="banner banner-warn" role="alert">
            <span className="banner-ico" aria-hidden="true">
              !
            </span>
            <span>
              你有 <strong>{pendingInvites.length}</strong> 条待处理的入队邀请：
              {pendingInvites.map((team, index) => (
                <span key={team.id}>
                  {index > 0 ? '、' : ' '}
                  <a
                    href="#teams"
                    className="small"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedId(team.id);
                    }}
                  >
                    「{team.name}」
                  </a>
                </span>
              ))}
              （点击队伍名后，在成员列表点「接受邀请」）
            </span>
          </div>
        );
      })()}

      {/* 已有队伍时，创建表单默认收起，把首屏让给队伍本身 */}
      <Collapsible
        title="创建队伍"
        hint="起个名字即可，至少 2 人接受后确认成队"
        defaultOpen={teams.length === 0}
      >
        <form className="stack" onSubmit={handleCreate} noValidate>
          {createError !== null ? (
            <div className="banner banner-danger" role="alert">
              <span className="banner-ico" aria-hidden="true">
                !
              </span>
              <span className="wrap-anywhere">{createError}</span>
            </div>
          ) : null}
          <div className="grid-2">
            <div className="field">
              <label className="field-label" htmlFor="team-name">
                队伍名称（必填）
              </label>
              <input
                id="team-name"
                ref={nameRef}
                className="input"
                value={teamName}
                maxLength={30}
                placeholder="如：立信 × 东华 三人组"
                onChange={(event) => setTeamName(event.target.value)}
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="team-event-code">
                赛事码（选填）
              </label>
              <input
                id="team-event-code"
                className="input"
                value={teamEventCode}
                placeholder="如 EV2026"
                onChange={(event) => setTeamEventCode(event.target.value.toUpperCase())}
              />
              <span className="field-hint">绑定赛事码后，同赛事的画像才互相可见。</span>
            </div>
          </div>
          <div className="row">
            <Button type="submit" variant="primary" loading={createTeam.isPending}>
              {createTeam.isPending ? '正在创建…' : '创建队伍'}
            </Button>
          </div>
        </form>
      </Collapsible>

      {teams.length === 0 ? (
        <EmptyState
          icon="⚑"
          title="你还没有任何队伍"
          description="先创建一支队伍（填个名字即可），再去候选人池记下想邀请的同学 userId，邀请后让对方在自己的账号里点「接受邀请」。"
          action={
            <Button variant="primary" onClick={() => nameRef.current?.focus()}>
              去填写队伍名称
            </Button>
          }
        />
      ) : (
        <div className="stack">
          <Card title="我的队伍列表" subtitle={`共 ${teams.length} 支（点击切换查看）`}>
            <div className="row-tight">
              {teams.map((team) => (
                <button
                  key={team.id}
                  type="button"
                  className={activeTeam !== null && activeTeam.id === team.id ? 'chip on' : 'chip'}
                  aria-pressed={activeTeam !== null && activeTeam.id === team.id}
                  onClick={() => setSelectedId(team.id)}
                >
                  {team.name}
                  <span className="chip-cat">· {teamStatusLabel(team.status)}</span>
                </button>
              ))}
            </div>
          </Card>

          {activeTeam !== null ? (
            <Card
              title={activeTeam.name}
              subtitle={`队伍 id：${activeTeam.id} · 创建于 ${formatDateTime(activeTeam.createdAt)}${
                activeTeam.eventCode ? ` · 赛事码 ${activeTeam.eventCode}` : ''
              }`}
              actions={
                <>
                  <Tag tone={activeTeam.status === 'confirmed' ? 'ok' : 'warn'}>
                    {teamStatusLabel(activeTeam.status)}
                  </Tag>
                  <Tag tone="muted">
                    已接受 {acceptedCount}/{activeTeam.members.length}
                  </Tag>
                  {activeTeam.direction ? (
                    <Tag tone="primary" title={`由 ${activeTeam.direction.chosenByName} 在工作台方向室选定`}>
                      方向：{activeTeam.direction.label}
                    </Tag>
                  ) : null}
                </>
              }
            >
              <div className="tab-scroll" role="tablist" aria-label="队伍视图切换">
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'info'}
                  className={tab === 'info' ? 'chip on' : 'chip'}
                  onClick={() => setTab('info')}
                >
                  队伍信息
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'chat'}
                  className={tab === 'chat' ? 'chip on' : 'chip'}
                  onClick={() => setTab('chat')}
                >
                  队伍群聊
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'gap'}
                  className={tab === 'gap' ? 'chip on' : 'chip'}
                  onClick={() => setTab('gap')}
                >
                  缺口雷达
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'hall'}
                  className={tab === 'hall' ? 'chip on' : 'chip'}
                  onClick={() => setTab('hall')}
                >
                  组队大厅
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'board'}
                  className={tab === 'board' ? 'chip on' : 'chip'}
                  disabled={projectId === null}
                  title={projectId === null ? '确认成队后开放项目看板' : undefined}
                  onClick={() => setTab('board')}
                >
                  项目看板
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'workbench'}
                  className={tab === 'workbench' ? 'chip on' : 'chip'}
                  onClick={() => setTab('workbench')}
                >
                  工作台
                </button>
              </div>

              {tab === 'chat' ? (
                <TeamChatPanel teamId={activeTeam.id} teamName={activeTeam.name} />
              ) : null}

              {tab === 'workbench' ? (
                <WorkbenchPanel team={activeTeam} meId={me?.id ?? ''} projectId={projectId} />
              ) : null}

              {tab === 'gap' ? (
                <GapPanel query={gapQuery} />
              ) : null}

              {tab === 'hall' ? <TeamHallPanel /> : null}

              {tab === 'info' ? (
                <>
              {me !== null && activeTeam.ownerId === me.id && activeTeam.status === 'forming' ? (
                <div className="row-tight">
                  <span className="small muted">准入档（组队大厅）：</span>
                  <select
                    className="select"
                    aria-label="准入档"
                    value={activeTeam.joinPolicy ?? 'approval'}
                    disabled={setPolicy.isPending}
                    onChange={(event) =>
                      setPolicy.mutate({
                        teamId: activeTeam.id,
                        policy: event.target.value as 'open' | 'approval' | 'invite_only',
                      })
                    }
                  >
                    <option value="open">免审核（申请即入队）</option>
                    <option value="approval">需审批（队长通过）</option>
                    <option value="invite_only">仅邀请（不进大厅）</option>
                  </select>
                  <span className="field-hint">免审核/需审批会出现在「组队大厅」，仅邀请只走邀请制。</span>
                </div>
              ) : null}
              <div className="stack-sm">
                <span className="card-sub">成员名单（成员状态由后端维护）</span>
                {activeTeam.members.length === 0 ? (
                  <p className="muted small">还没有成员？这通常意味着队伍刚创建。用下面的表单按 userId 邀请。</p>
                ) : (
                  activeTeam.members.map((member) => {
                    const isMe = me !== null && me.id === member.userId;
                    const canEditRole =
                      me !== null && activeTeam.ownerId === me.id && activeTeam.status === 'forming';
                    const rolePending = setRole.isPending && setRole.variables?.userId === member.userId;
                    return (
                      <div className="row" key={member.userId}>
                        <strong>{memberNameOf(activeTeam, member.userId)}</strong>
                        {canEditRole ? (
                          <select
                            className="select"
                            aria-label={`${memberNameOf(activeTeam, member.userId)} 的角色`}
                            value={member.role}
                            disabled={rolePending}
                            title="确认成队后角色冻结，不可再改（T71）"
                            onChange={(event) =>
                              setRole.mutate({
                                teamId: activeTeam.id,
                                userId: member.userId,
                                role: event.target.value as TeamRole,
                              })
                            }
                          >
                            {ROLE_ORDER.map((role) => (
                              <option key={role} value={role}>
                                {ROLE_LABELS[role]}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <Tag tone={member.role === 'leader' ? 'primary' : 'default'}>{roleLabel(member.role)}</Tag>
                        )}
                        <Tag
                          tone={member.status === 'accepted' ? 'ok' : member.status === 'declined' ? 'danger' : 'warn'}
                        >
                          {MEMBER_STATUS_LABELS[member.status]}
                        </Tag>
                        {isMe ? <Tag tone="school">我</Tag> : null}
                        <span className="muted small">
                          <code className="mono">{member.userId}</code>
                        </span>
                        {!isMe && member.status === 'accepted' ? (
                          <span className="row-tight right">
                            <Button
                              size="sm"
                              onClick={() =>
                                chatApi
                                  .openDirect(member.userId)
                                  .then((data) => {
                                    void queryClient.invalidateQueries({ queryKey: qk.conversations() });
                                    navigate(`/messages?c=${encodeURIComponent(data.conversation.id)}`);
                                  })
                                  .catch((error: unknown) => toast.error('发起私聊失败', toUserMessage(error)))
                              }
                            >
                              发消息
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                chatApi
                                  .requestFriend(member.userId)
                                  .then(() => {
                                    queryClient.invalidateQueries({ queryKey: qk.friends() });
                                    toast.success('好友申请已发出', '对方通过后你们可以互相私聊');
                                  })
                                  .catch((error: unknown) => toast.error('申请失败', toUserMessage(error)))
                              }
                            >
                              加好友
                            </Button>
                          </span>
                        ) : null}
                        {isMe && member.status === 'invited' ? (
                          <Button
                            variant="primary"
                            size="sm"
                            className="right"
                            loading={accept.isPending}
                            onClick={() => accept.mutate({ teamId: activeTeam.id, userId: member.userId })}
                          >
                            接受邀请
                          </Button>
                        ) : null}
                        {!isMe && member.status === 'invited' ? (
                          <span className="muted small right">等待对方登录后接受</span>
                        ) : null}
                        {!isMe && member.status === 'requested' && canEditRole ? (
                          <span className="row-tight right">
                            <Button
                              variant="primary"
                              size="sm"
                              loading={approveReq.isPending}
                              onClick={() =>
                                approveReq.mutate({ teamId: activeTeam.id, userId: member.userId })
                              }
                            >
                              通过
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              loading={declineReq.isPending}
                              onClick={() =>
                                declineReq.mutate({ teamId: activeTeam.id, userId: member.userId })
                              }
                            >
                              拒绝
                            </Button>
                          </span>
                        ) : null}
                      </div>
                    );
                  })
                )}
                {activeTeam.members.some((member) => member.status === 'invited') ? (
                  <p className="field-hint">被邀请的本人登录后即可接受邀请。</p>
                ) : null}
              </div>

              <hr className="divider" />

              <form className="stack" onSubmit={handleInvite} noValidate>
                {inviteError !== null ? (
                  <div className="banner banner-danger" role="alert">
                    <span className="banner-ico" aria-hidden="true">
                      !
                    </span>
                    <span className="wrap-anywhere">{inviteError}</span>
                  </div>
                ) : null}
                <div className="grid-2">
                  <div className="field">
                    <label className="field-label" htmlFor="invite-user-id">
                      对方的邀请 ID（推荐）
                    </label>
                    <input
                      id="invite-user-id"
                      className="input"
                      value={inviteUserId}
                      placeholder="形如 CM-7K2M9Q"
                      onChange={(event) => {
                        setInviteUserId(event.target.value);
                        // 输入变了就作废上一次查找结果，避免「查到 A 却邀请了 B」
                        setLookupResult(null);
                        setLookupError(null);
                      }}
                    />
                    <span className="field-hint">
                      让对方在「个人资料」页复制 TA 的邀请 ID 发给你；也可以直接粘贴 userId（UUID，如从候选人池复制）。
                    </span>
                  </div>
                  <div className="field">
                    <label className="field-label" htmlFor="invite-role">
                      角色
                    </label>
                    <select
                      id="invite-role"
                      className="select"
                      value={inviteRole}
                      onChange={(event) => setInviteRole(event.target.value as TeamRole)}
                    >
                      {ROLE_ORDER.map((role) => (
                        <option key={role} value={role}>
                          {ROLE_LABELS[role]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {lookupError !== null ? (
                  <div className="banner banner-danger" role="alert">
                    <span className="banner-ico" aria-hidden="true">
                      !
                    </span>
                    <span className="wrap-anywhere">{lookupError}</span>
                  </div>
                ) : null}

                {lookupResult !== null ? (
                  <div className="card tight">
                    <div className="row-tight">
                      <Tag tone="ok">已锁定</Tag>
                      <strong>{lookupResult.name}</strong>
                      <Tag tone="school">{lookupResult.school}</Tag>
                      {lookupResult.publicId !== null ? <Tag tone="primary">{lookupResult.publicId}</Tag> : null}
                    </div>
                    <p className="field-hint">
                      {lookupResult.headline ?? '（对方还没填一句话简介）'}
                      {lookupResult.skills.length > 0
                        ? ` · 技能：${lookupResult.skills.map((skill) => skillLabel(skill)).join('、')}`
                        : ''}
                      {lookupResult.weeklyHours !== null ? ` · 每周可投入 ${lookupResult.weeklyHours} 小时` : ''}
                    </p>
                  </div>
                ) : null}

                <div className="row">
                  <Button type="button" variant="secondary" onClick={handleLookup} loading={lookup.isPending}>
                    {lookup.isPending ? '查找中…' : '按 ID 查找'}
                  </Button>
                  <Button type="submit" variant="primary" loading={invite.isPending}>
                    {lookupResult !== null ? `邀请 ${lookupResult.name}` : '发送邀请'}
                  </Button>
                </div>
              </form>

              <hr className="divider" />

              <RecruitEditor team={activeTeam} canEdit={me !== null && activeTeam.ownerId === me.id && activeTeam.status === 'forming'} />

              <hr className="divider" />

              <div className="row">
                {me !== null && activeTeam.ownerId === me.id ? (
                  <>
                    <Button
                      variant="primary"
                      onClick={() => setConfirmOpen(true)}
                      disabled={activeTeam.status === 'confirmed'}
                    >
                      {activeTeam.status === 'confirmed' ? '已确认成队' : '确认成队'}
                    </Button>
                    <span className="field-hint">
                      {activeTeam.status === 'confirmed'
                        ? '队伍已确认；切到「项目看板」查看阶段待办。'
                        : `已接受 ${acceptedCount} 人，确认后生成项目 + 4 条阶段待办（不可撤销）。`}
                    </span>
                  </>
                ) : (
                  <span className="field-hint">
                    {activeTeam.status === 'confirmed' ? '队伍已确认，可到「项目看板」查看阶段待办。' : '确认成队由队长操作。'}
                  </span>
                )}
              </div>
                </>
              ) : null}

              {tab === 'board' && projectId !== null ? (
                <div className="stack" id="team-board">
                  <div className="row">
                    <span className="card-sub">阶段进度（按 phase 统计完成度）</span>
                    <Link className="btn btn-ghost btn-sm right" to={`/tasks?project=${encodeURIComponent(projectId)}`}>
                      在阶段待办页打开
                    </Link>
                  </div>
                  {projectTasksQuery.isPending ? (
                    <Loading label="正在读取项目待办…" rows={2} />
                  ) : projectTasksQuery.isError ? (
                    <ErrorState
                      error={projectTasksQuery.error}
                      title="读取项目待办失败"
                      onRetry={() => void projectTasksQuery.refetch()}
                    />
                  ) : (
                    <>
                      <PhaseBar tasks={projectTasksQuery.data?.tasks ?? []} />
                      <Kanban
                        tasks={projectTasksQuery.data?.tasks ?? []}
                        projectId={projectId}
                        members={activeTeam.members
                          .filter((member) => member.status === 'accepted')
                          .map((member) => ({ userId: member.userId, name: memberNameOf(activeTeam, member.userId) }))}
                        canAssign={me !== null && activeTeam.ownerId === me.id}
                      />
                    </>
                  )}
                </div>
              ) : null}
            </Card>
          ) : null}
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        danger
        title="确认成队？"
        description={
          <>
            <p>
              确认后队伍成为「已成队」，自动生成项目与 <strong>4 条阶段待办</strong>。
            </p>
            <p>当前已接受 {acceptedCount} 人。确认成队属于不可撤销操作，请确认成员都到齐了。</p>
          </>
        }
        confirmText="确认成队"
        pending={confirmTeam.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          if (activeTeam !== null) confirmTeam.mutate(activeTeam.id);
        }}
      />
    </div>
  );
}